// The University of Chicago: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - UChicago's Common Data Set 2025-2026 (CDS_2025-2026_to_publish-1.pdf
//    from data.uchicago.edu): C1 applicants/admits/enrollees for Fall 2025;
//    C8 test policy; C21/C22 early plans; G1/G5 2026-2027 costs. Quirk: many
//    page footers still read "Common Data Set 2024-2025", but the file name,
//    its first page and its contents (Fall 2025 counts, 2026-2027 costs) are
//    2025-2026. The C8 page cuts off the term its question names. The C1
//    residency table and the C21 early decision counts are left blank.
//  - collegeadmissions.uchicago.edu: Application (the four plans with dated
//    deadlines for this cycle, Nov 2, 2026 and Jan 4, 2027, and the "Changing
//    Your Decision Plan" note), Required Materials (No Harm testing policy).
//  - College Scorecard, school id 144050 (net price by family income). The
//    DEMO_KEY API was over its rate limit on 2026-10-01, so the figures were
//    read from the Scorecard's own school page, which shows the same latest
//    year the API serves.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UChicago Common Data Set 2025-2026',
        url: 'https://data.uchicago.edu/files/2026/08/CDS_2025-2026_to_publish-1.pdf',
        read: '2026-10-01',
    },
    application: {
        label: 'UChicago College Admissions, Application',
        url: 'https://collegeadmissions.uchicago.edu/apply/application/',
        read: '2026-10-01',
    },
    tests: {
        label: 'UChicago College Admissions, Required Materials (No Harm Testing Policy)',
        url: 'https://collegeadmissions.uchicago.edu/apply/application/required-materials/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?144050-University-of-Chicago',
        read: '2026-10-15',
    },
    coa: {
        label: 'UChicago Financial Aid, Undergraduate Costs: Cost of Attendance for 2026-2027',
        url: 'https://financialaid.uchicago.edu/undergraduate/how-aid-works/undergraduate-costs/',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'university-of-chicago',
    kbId: 'university_of_chicago',
    name: 'University of Chicago',
    shortName: 'UChicago',
    aliases: ['U of C'],
    location: 'Chicago, IL',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (UChicago identity colors); decoration only.
    branding: {
        primary: '#800000',
        secondary: '#A6A6A6',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_chicago_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 45533,
        admitted: 2039,
        enrolled: 1786,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UChicago\'s Common Data Set reports one first-year acceptance rate for the College, with no rate by major. You apply to the College, not to a major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UChicago offers early decision and early action, but the Common Data Set leaves its early decision counts blank, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 2', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 2', decision: 'Mid-December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 4', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'Late March', binding: false },
        ],
        note: 'Admitted in Early Decision I, you reply by mid-January; in Early Decision II, by mid-March; in Early Action or Regular Decision, by May 1.',
        source: 'application',
        binding: 'Early Decision I and II are binding; Early Action and Regular Decision are not. You can switch from Early Action to Early Decision I by December 1, and a deferred Early Action or Regular Decision applicant can switch to Early Decision II by January 15, by sending an Early Decision agreement form.',
        bindingSource: 'application',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Sending an SAT or ACT score is optional. Under UChicago\'s No Harm policy, a score you send is used only if it helps your application; a score that could hurt it is not considered. You may self-report scores and send official reports only if you enroll.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'application',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 75960 },
            { item: 'Student services fee', amount: 1677 },
            { item: 'UPASS fee', amount: 318 },
            { item: 'Food and housing (on campus)', amount: 21414 },
            { item: 'Books and course materials', amount: 1800 },
            { item: 'Personal expenses', amount: 1950 },
            { item: 'Travel allowance', amount: 702 },
        ],
        total: 103821,
        note: 'On-campus students. Students who start in 2026 also pay a $720 class fee in each of their first two years. Health insurance is not included.',
        source: 'coa',
        netPrice: {
            average: 16776,
            byIncome: [
                { income: '$0 to $30,000', amount: 394 },
                { income: '$30,001 to $48,000', amount: 1148 },
                { income: '$48,001 to $75,000', amount: 2800 },
                { income: '$75,001 to $110,000', amount: 7744 },
                { income: 'Over $110,000', amount: 49769 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UChicago\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
